import { Inject, Injectable } from '@angular/core';
import { PL_API_CONFIG, PlAPIConfig } from 'pl-core-services';
import { Account, AccountApi } from '@common/models/account';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, map, Observable } from 'rxjs';
import { DateTime } from 'luxon';
import { environment } from '@environments/environment';
import { ApiPaginatedResult } from '@ts/api-result';
import { BAASign } from '../models/baa-sign';
import { AccountClientExport, Client, User } from '../models';

interface ExportPayload {
  clientIds?: string[];
  providerIds?: string[];
}

const EXPORT_URL = `${environment.apps.kanga}/api/v1/data/`;

@Injectable({ providedIn: 'root' })
export class PlAccountService {
  readonly accountsUrl: string;

  constructor(
    private client: HttpClient,
    @Inject(PL_API_CONFIG) config: PlAPIConfig
  ) {
    this.accountsUrl = `${config.kanga}/api/v1/accounts/`;
  }

  getClients(accountUuid: string): Observable<Client[]> {
    const url = `${this.accountsUrl}${accountUuid}/clients`;
    return this.client.get<Client[]>(url);
  }

  getProviders(accountUuid: string): Observable<User[]> {
    const url = `${this.accountsUrl}${accountUuid}/users`;
    return this.client.get<User[]>(url);
  }

  async getAccount(accountUuid?: string): Promise<Account> {
    const params = accountUuid ? { uuid: accountUuid } : {};
    const accountsApi = await firstValueFrom(
      this.client.get<AccountApi[]>(this.accountsUrl, { params })
    );
    const accounts: Account[] = accountsApi.map((a) => ({
      ...a,
      deactivation_date: a.deactivation_date
        ? DateTime.fromISO(a.deactivation_date)
        : null,
      expiration_date: a.expiration_date
        ? DateTime.fromISO(a.expiration_date)
        : null,
    }));
    if (accounts.length !== 1) {
      return null;
    }

    return accounts[0];
  }

  isAccountBaaSigned(accountUuid: string) {
    const url = `${this.accountsUrl}${accountUuid}/baa_signed`;
    return this.client.get<BAASign>(url).pipe(map((res) => res.baa_signed));
  }

  extendDeactivation(accountUuid: string, date: DateTime): Observable<any> {
    const url = `${this.accountsUrl}${accountUuid}/extend_deactivation`;
    return this.client.put<any>(url, { deactivation_date: date.toISODate() });
  }

  exportClientsData(
    accountId: string,
    { providerIds, clientIds }: ExportPayload = {}
  ): Observable<AccountClientExport> {
    const url = `${EXPORT_URL}${accountId}`;
    const body: any = {};

    if (providerIds) body.providers_id = providerIds;

    if (clientIds) body.clients_id = clientIds;

    return this.client.post<AccountClientExport>(url, body);
  }

  getExportClientsStatus(accountId: string) {
    const url = `${EXPORT_URL}${accountId}`;
    return this.client
      .get<ApiPaginatedResult<AccountClientExport>>(url, {
        params: {
          limit: 1,
        },
      })
      .pipe(map((list) => list.results[0]));
  }

  discardExport(accountId: string, exportId: string) {
    const url = `${EXPORT_URL}${accountId}/${exportId}`;
    return this.client.patch<{ discarded: true }>(url, {
      discarded: true,
    });
  }
}
